/*
 * Copyright (C) 2026 to Present Applause App Quality, Inc. All rights reserved.
 */

import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostBinding, HostListener, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { Observable, Subscription } from 'rxjs';
import { DelightSupportService } from '@ng-falcon/delight-support/delight-support.service';
import { NotificationService } from '@ng-falcon/notification/notification.service';
import { NotificationType } from '@ng-falcon/notification/notification-type';
import { NotificationConfig } from '@ng-falcon/notification/notification-config';
import {
  TESTER_NAVIGATION_SIDEBAR_SERVICE,
  TesterNavigationSidebarService,
  TesterNavigationSidebarUrls,
  TesterNavigationSidebarUser,
} from './tester-navigation-sidebar.service';
import { TesterNavigationSidebarExpansionService } from './tester-navigation-sidebar-expansion.service';
import { TesterNavigationSidebarLogoComponent } from './tester-navigation-sidebar-logo/tester-navigation-sidebar-logo.component';
import { TesterNavigationSidebarMenuItemComponent } from './tester-navigation-sidebar-menu-item/tester-navigation-sidebar-menu-item.component';
import { TesterNavigationSidebarMenuItemChildComponent } from './tester-navigation-sidebar-menu-item-child/tester-navigation-sidebar-menu-item-child.component';
import { TesterNavigationMobileMenuService } from '../tester-navigation-mobile-menu.service';
import { TesterNavigationSidebarAccountComponent } from './tester-navigation-sidebar-account/tester-navigation-sidebar-account.component';

@Component({
  selector: 'ngf-tester-navigation-sidebar',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    TesterNavigationSidebarLogoComponent,
    TesterNavigationSidebarMenuItemComponent,
    TesterNavigationSidebarMenuItemChildComponent,
    TesterNavigationSidebarAccountComponent,
  ],
  providers: [TesterNavigationSidebarExpansionService],
  templateUrl: './tester-navigation-sidebar.component.html',
  styleUrls: ['./tester-navigation-sidebar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TesterNavigationSidebarComponent implements OnInit, OnDestroy {
  @Input() logoSrc = 'assets/images/uTestLogoNav.svg';
  @Input() compactLogoSrc = 'assets/images/uTestLogoU.svg';

  @HostBinding('attr.role') readonly role = 'navigation';
  @HostBinding('attr.aria-label') readonly ariaLabel = 'Tester';
  @HostBinding('class.mobile-open') mobileOpen = false;

  readonly urls$: Observable<TesterNavigationSidebarUrls>;
  readonly user$: Observable<TesterNavigationSidebarUser>;
  readonly anyExpanded$: Observable<boolean>;
  readonly isHomeActive$: Observable<boolean>;
  readonly notificationsActive$: Observable<boolean>;

  chatLoading = false;

  private mobileMenuSub = Subscription.EMPTY;
  private focusTimeoutId: ReturnType<typeof setTimeout> | null = null;

  constructor(
    @Inject(TESTER_NAVIGATION_SIDEBAR_SERVICE) sidebarService: TesterNavigationSidebarService,
    private expansionService: TesterNavigationSidebarExpansionService,
    private mobileMenuService: TesterNavigationMobileMenuService,
    private supportChatService: DelightSupportService,
    private notificationService: NotificationService,
    private cdr: ChangeDetectorRef,
    private el: ElementRef<HTMLElement>,
  ) {
    this.urls$ = sidebarService.urls$;
    this.user$ = sidebarService.user$;
    this.anyExpanded$ = expansionService.anyExpanded$;
    this.isHomeActive$ = sidebarService.isActive$('home');
    this.notificationsActive$ = sidebarService.isActive$('notificationsUrl');
  }

  ngOnInit(): void {
    this.mobileMenuSub = this.mobileMenuService.isOpen$.subscribe(open => {
      this.mobileOpen = open;
      this.cdr.markForCheck();
      if (open) {
        // Defer past the current CD cycle so the panel has finished sliding
        // into view before focus is moved inside it.
        if (this.focusTimeoutId !== null) {
          clearTimeout(this.focusTimeoutId);
        }
        this.focusTimeoutId = setTimeout(() => {
          this.focusTimeoutId = null;
          const first = this.el.nativeElement.querySelector<HTMLElement>(
            '.sidebar-content a[href], .sidebar-content button:not([disabled])',
          );
          first?.focus();
        }, 300);
      }
    });
  }

  ngOnDestroy(): void {
    if (this.focusTimeoutId !== null) {
      clearTimeout(this.focusTimeoutId);
      this.focusTimeoutId = null;
    }
    this.mobileMenuSub.unsubscribe();
  }

  closeMobileMenu(): void {
    this.mobileMenuService.close();
  }

  async toggleSupportChat(): Promise<void> {
    if (this.supportChatService.hasMessengerInstance) {
      this.mobileMenuService.close();
      this.supportChatService.openMessenger();
      return;
    }

    this.chatLoading = true;
    this.cdr.markForCheck();

    const chatLaunched = await this.supportChatService.launchChat();

    this.chatLoading = false;
    this.mobileMenuService.close();

    if (!chatLaunched) {
      this.notificationService.show<NotificationConfig>({
        text: 'Failed to launch support chat. Please try again later.',
        type: NotificationType.DANGER,
        lifetime: -1,
      });
    }
    this.cdr.detectChanges();
  }

  /** Close the mobile drawer when the user navigates via a sidebar link. */
  @HostListener('click', ['$event.target'])
  onSidebarClick(target: EventTarget | null): void {
    if (!this.mobileOpen || !(target instanceof Element)) {
      return;
    }
    if (target.closest('a[href]')) {
      this.mobileMenuService.close();
    }
  }

  /** Click on the bottom toggle: opens the active menu-item's flyout, or
   *  closes any open flyout. The active menu-item subscribes to
   *  toggleActive$ and flips its own expanded state. */
  toggleActiveFlyout(event: MouseEvent): void {
    event.stopPropagation();
    this.expansionService.toggleActive();
  }

  /** Single document-level listener at the sidebar root. Each open flyout
   *  closes when the click target lands outside the sidebar host — replaces
   *  the per-menu-item HostListener so we register one handler instead of
   *  one per row. */
  @HostListener('document:click', ['$event.target'])
  onDocumentClick(target: EventTarget | null): void {
    if (!(target instanceof Element)) {
      return;
    }
    if (target.closest('ngf-tester-navigation-sidebar')) {
      return;
    }
    this.expansionService.closeAll();
  }
}
